import * as React from 'react';
import { type LeadCall, useLeadCallHistory } from './useLeadCallHistory';

/** What the lead's call history says about one phone number. */
export type DialAttempts = {
	/** How many past calls dialed this number. */
	count: number;
	/** Most recent attempt, for the row tooltip. ISO string as it comes off the call. */
	lastAttemptAt?: string;
};

export function collectDialAttempts(phoneCalls: readonly LeadCall[]): Map<string, DialAttempts> {
	const attempts = new Map<string, DialAttempts>();
	for (const view of phoneCalls) {
		const call = view.phoneCall;
		const e164 = call?.phoneNumber?.e164;
		if (!e164) continue;
		const at = call.startTime ?? call.creationDate;
		const existing = attempts.get(e164);
		if (!existing) {
			attempts.set(e164, { count: 1, lastAttemptAt: at });
			continue;
		}
		existing.count += 1;
		if (at && (!existing.lastAttemptAt || Date.parse(at) > Date.parse(existing.lastAttemptAt))) {
			existing.lastAttemptAt = at;
		}
	}
	return attempts;
}

export type UseDialedNumbersApi = {
	/** Numbers dialed since this lead was served. */
	sessionDialed: ReadonlySet<string>;
	/** History only, so an optimistic dial does not double-count. */
	dialAttempts: ReadonlyMap<string, DialAttempts>;
	markDialed: (e164: string) => void;
};

export function useDialedNumbers(companyId: string | undefined): UseDialedNumbersApi {
	const { data } = useLeadCallHistory(companyId);
	const [sessionDialed, setSessionDialed] = React.useState<ReadonlySet<string>>(() => new Set<string>());

	React.useEffect(() => {
		setSessionDialed(new Set<string>());
	}, [companyId]);

	const dialAttempts = React.useMemo(() => collectDialAttempts(data ?? []), [data]);

	const markDialed = React.useCallback((e164: string) => {
		setSessionDialed(prev => {
			if (prev.has(e164)) return prev;
			const next = new Set(prev);
			next.add(e164);
			return next;
		});
	}, []);

	return { sessionDialed, dialAttempts, markDialed };
}
